import React, { useState } from "react";
import Select from "react-select";
import addFees from '../../../../../assets/images/add_fees.svg';
import removeFees from '../../../../../assets/images/remove_fees.svg';
import { formatDecimal as fmt } from '../helpers/paymentsHelper';
import { PURPLE, GREY, EyeToggleIcon, money } from './Weekly/weeklyTableHelpers';

// Discretionary billing car-type options for the vehicle dropdown. `value` keys
// MUST match the GroupedTrip / Car / DriverBilling `car_type` enum so the backend
// override slots into the existing rate lookups. "Reset" (empty value) sits at the
// top and clears the override back to the physical vehicle. executive_sedan is
// omitted — premium_sedan is the single sedan class and reads simply as "Sedan".
// See ADR discretionary_billing_override.md.
const VEHICLE_OPTIONS = [
  { value: "", label: "Reset" },
  { value: "premium_sedan", label: "Sedan" },
  { value: "van", label: "Van" },
  { value: "minibus", label: "Minibus" },
  { value: "hatchback", label: "Hatchback" },
];

// Human label for an effective billing car-type key (e.g. "premium_sedan" →
// "Sedan"), used for the read-only display outside edit mode. Excludes the Reset
// action row.
const carTypeLabel = (value) =>
  VEHICLE_OPTIONS.find((opt) => opt.value === value && opt.value !== "")?.label || null;

// Row action buttons share the Weekly Overview styling: a 41x41, 5px-radius solid
// tile with a centred icon (see Weekly InvoicingTable). Colours are the design
// tokens — green save, purple (shared PURPLE) edit/no-charge, grey (shared GREY)
// view/revert, red cancel.
const BTN_GREEN = "#5DA876";
const BTN_RED = "#D64545";
const ACTION_BTN_CLASS = "inline-flex items-center justify-center shrink-0";
const actionBtnStyle = (background) => ({ width: "41px", height: "41px", borderRadius: "5px", background });

// react-select styling for the inline vehicle dropdown: a compact white field
// with a 5px radius matching the design, and a portalled menu so the table's
// overflow container never clips it.
const vehicleSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "34px",
    minWidth: "132px",
    borderColor: "rgba(110,108,113,0.3)",
    borderRadius: "5px",
    boxShadow: "none",
    fontFamily: "Ubuntu, system-ui, sans-serif",
    fontSize: "14px",
    "&:hover": { borderColor: "rgba(110,108,113,0.3)" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base) => ({ ...base, fontFamily: "Ubuntu, system-ui, sans-serif", fontSize: "14px" }),
  singleValue: (base) => ({ ...base, fontFamily: "Ubuntu, system-ui, sans-serif", fontSize: "14px" }),
};

// The warning-triangle SVG at a given fill. One shape, four colours.
const warningTriangle = (fill) => (
  <svg width="27" height="24" viewBox="0 0 27 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M26.6952 20.625C27.5625 22.125 26.4749 24 24.7451 24H2.25296C0.523153 24 -0.559732 22.1203 0.302826 20.625L11.5489 1.125C12.4161 -0.375 14.5819 -0.375 15.4492 1.125L26.6952 20.625ZM13.499 16.5938C12.3083 16.5938 11.3426 17.5594 11.3426 18.75C11.3426 19.9406 12.3083 20.9062 13.499 20.9062C14.6897 20.9062 15.6554 19.9406 15.6554 18.75C15.6554 17.5594 14.6897 16.5938 13.499 16.5938ZM11.4504 8.84531L11.7973 15.2203C11.8114 15.5203 12.0599 15.75 12.3599 15.75H14.6335C14.9335 15.75 15.1773 15.5156 15.196 15.2203L15.5429 8.84531C15.5617 8.52187 15.3038 8.25 14.9804 8.25H12.0083C11.6848 8.25 11.427 8.52187 11.4458 8.84531H11.4504Z"
      fill={fill}
    />
  </svg>
);

// A flag icon with a hover tooltip explaining what it means. The native title is kept as an
// accessibility fallback; the styled span is what a mouse user sees. Tailwind here is v1, so
// the box uses inline styles for exact tokens (see the app's tooltip.css for the same look).
const FlagTooltip = ({ label, testId, children }) => (
  <span className="group relative inline-flex items-center" data-testid={testId} title={label}>
    {children}
    <span
      role="tooltip"
      className="hidden group-hover:block"
      style={{
        position: 'absolute', top: 'calc(100% + 5px)', left: 0, transform: 'translateX(-30%)',
        width: 'max-content', maxWidth: '220px', background: '#fff', color: '#6E6C71',
        padding: '8px', fontSize: '12px', borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 999, fontWeight: 400,
      }}
    >
      {label}
    </span>
  </span>
);

// Tracking/billing status icon for a grouped-trip row, each with a hover tooltip. Uncosted
// (billing not configured) takes precedence, then the tracking-accuracy flags. Extracted as
// guard clauses so the row map's cognitive complexity stays in limit.
const trackingIcon = (item) => {
  if (item.uncosted_reason) {
    return (
      <FlagTooltip label={item.uncosted_reason || 'Billing not configured'} testId="payments-uncosted-flag">
        {warningTriangle('#FF0000')}
      </FlagTooltip>
    );
  }

  if (item.tracking === 'accurate' || item.tracking === 'untracked') {
    const label = item.tracking === 'accurate'
      ? 'Actual distance matches the planned tier'
      : 'Not tracked — billed on the planned distance';
    return (
      <FlagTooltip label={label} testId="payments-accurate-flag">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path d="M9 19.2l-5.2-5.2 1.4-1.4L9 16.4l9.8-9.8 1.4 1.4L9 19.2z" fill="green" />
        </svg>
      </FlagTooltip>
    );
  }

  if (item.tracking === 'inaccurate') {
    return (
      <FlagTooltip
        label="Actual distance fell in a different tier than planned — billed on the actual distance"
        testId="payments-inaccurate-flag"
      >
        {warningTriangle('#F3986D')}
      </FlagTooltip>
    );
  }

  if (item.tracking === 'incomplete') {
    return (
      <FlagTooltip
        label="Trip not yet completed — billed on the planned distance as an estimate"
        testId="payments-incomplete-flag"
      >
        {warningTriangle('#FF0000')}
      </FlagTooltip>
    );
  }

  return null;
};

const CompaniesTable = ({
  data,
  updateSiteOverviewTrips,
  currentPage,
  fetchSiteOverview,
  handleNext,
  handleBack,
  isAdmin,
  paymentPlatformMvp,
  isFinance,
}) => {
  // Role-driven money visibility. Logistics specialists see NO money on Company
  // Overview — neither the client charge nor the driver payout (CU-86cb58zru,
  // requested by Operations 2026-08-13, reversing card 86cad7jw0's "LS see Driver
  // Pay only"). Finance roles (admin, company_admin) keep the client Billing
  // column with the flag on, admins keep the legacy Fare column with it off.
  //
  // Logistics reaches Company Overview independent of the flag, so the gate has to
  // hold in BOTH states — otherwise an LS on a flag-off session is handed the fare
  // through the legacy column. The server withholds these values as well; this
  // drops the columns so the page reads as intentional rather than empty.
  const showVehicleCluster = paymentPlatformMvp ? isFinance : isAdmin;
  const payColumn = paymentPlatformMvp
    ? isFinance
      ? { key: "billing", header: "Billing", value: (row) => money(row.billing) }
      : null
    : isAdmin
    ? { key: "fare", header: "Fare", value: (row) => fmt(row.fare) }
    : null;

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // edit table
  const [editingRowId, setEditingRowId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showNoChargeConfirm, setShowNoChargeConfirm] = useState({ show: false, item: null });

  const handleEditClick = (item) => {
    setEditingRowId(item.id);
    setFormData({
      actual_kms: item.actual_kms,
      comments: item.comments,
      // Seed with the effective type so an unchanged Save leaves it untouched.
      billing_car_type: item.billing_car_type,
    });
  };

  const handleCancelClick = () => {
    setEditingRowId(null);
  };

  const handleSaveClick = (item) => {
    const raw = formData.actual_kms || '';

    // Extract numeric part from string like "10", "10km", " 10 km", etc.
    const numericValue = parseFloat(raw.toString().replace(/[^\d.]/g, ''));

    const formattedFormData = {
      ...formData,
      actual_kms: isNaN(numericValue) ? '' : `${numericValue} km`
    };

    // Only send the billing override when the user actually changed the vehicle
    // type. Site overview returns the EFFECTIVE type (never null), so sending it
    // unchanged would silently materialise a NULL "no override" into an explicit
    // one — pinning the trip's billing type against future vehicle changes.
    if (formData.billing_car_type === item.billing_car_type) {
      delete formattedFormData.billing_car_type;
    }

    updateSiteOverviewTrips(item.id, formattedFormData);

    setEditingRowId(null);
  };

  const handleMarkNoCharge = async (item) => {
    try {
      await fetch(`/payments/mark_no_charge/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      setEditingRowId(null);
      // refresh current page data without resetting pagination
      fetchSiteOverview(currentPage);
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkNoChargeRestore = async (item) => {
    try {
      await fetch(`/payments/mark_no_charge_restore/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      setEditingRowId(null);
      // refresh current page data without resetting pagination
      fetchSiteOverview(currentPage);
    } catch (e) {
      console.error(e);
    }
  };

  const openNoChargeConfirm = (item) => {
    setShowNoChargeConfirm({ item, show: true });
  };

  const closeNoChargeConfirm = () => {
    setShowNoChargeConfirm({ item: null, show: false });
  };

  const handleInputChange = (e, field) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  // Stages a vehicle-type override selection into formData (committed on Save).
  // The empty-value "Reset" option clears the override.
  const handleVehicleSelect = (option) => {
    setFormData({ ...formData, billing_car_type: option ? option.value : "" });
  };

  // eof edit table

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...data.grouped_trip_data].sort((a, b) => {
    if (sortConfig.key) {
      if (sortConfig.direction === "asc") {
        return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
      } else {
        return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
      }
    }
    return 0;
  });

  // The vehicle-type cell has three modes (edit dropdown / MVP effective label /
  // legacy vehicle type). Extracted as a closure so the row map stays within its
  // cognitive-complexity budget; it still reads editingRowId/formData from scope.
  const renderVehicleTypeCell = (item) => {
    if (paymentPlatformMvp && editingRowId === item.id) {
      return (
        <div data-testid={`billing-car-type-select-${item.id}`}>
          <Select
            classNamePrefix="vehicle-select"
            options={VEHICLE_OPTIONS}
            value={VEHICLE_OPTIONS.find(
              (opt) => opt.value === (formData.billing_car_type || "")
            )}
            onChange={handleVehicleSelect}
            isSearchable={false}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={vehicleSelectStyles}
          />
        </div>
      );
    }
    if (paymentPlatformMvp) return carTypeLabel(item.billing_car_type) || item.vehicle_type;
    return item.vehicle_type;
  };

  return (
    <div className="w-full h-full">
      {/* Scroll region belongs to the table so the pager below it stays put, matching
          every sibling table in this suite. */}
      <div
        data-testid="company-overview-scroll"
        className="overflow-y-auto w-full scrollbar"
        style={{ maxHeight: "600px" }}
      >
        <table
          data-testid="company-overview-table"
          className="border-collapse w-full"
        >
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("tracking")}
            >
              Tracking{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "tracking" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "tracking" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("date")}
            >
              Date{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "date" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "date" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("weekday")}
            >
              Weekday{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "weekday" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "weekday" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("time")}
            >
              Time{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "time" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "time" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("type")}
            >
              Type{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "type" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "type" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("fleet")}
            >
              Fleet{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "fleet" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "fleet" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("driver")}
            >
              Driver{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "driver" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "driver" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            {showVehicleCluster && (
              <>
                <th
                  className="border-b p-2 cursor-pointer text-left"
                  onClick={() => handleSort("vehicle_type")}
                >
                  Vehicle{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="16"
                    height="32"
                    className="inline-block ml-1"
                  >
                    <path
                      d="M12 5l-5 5h10l-5-5z"
                      fill={
                        sortConfig.key === "vehicle_type" &&
                        sortConfig.direction === "asc"
                          ? "#745D96"
                          : "#9C9C9C"
                      }
                    />

                    <path
                      d="M12 19l5-5H7l5 5z"
                      fill={
                        sortConfig.key === "vehicle_type" &&
                        sortConfig.direction === "desc"
                          ? "#745D96"
                          : "#9C9C9C"
                      }
                    />
                  </svg>
                </th>
                <th
                  className="border-b p-2 cursor-pointer text-left"
                  onClick={() => handleSort("occupancy")}
                >
                  Occ{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="16"
                    height="32"
                    className="inline-block ml-1"
                  >
                    <path
                      d="M12 5l-5 5h10l-5-5z"
                      fill={
                        sortConfig.key === "occupancy" &&
                        sortConfig.direction === "asc"
                          ? "#745D96"
                          : "#9C9C9C"
                      }
                    />

                    <path
                      d="M12 19l5-5H7l5 5z"
                      fill={
                        sortConfig.key === "occupancy" &&
                        sortConfig.direction === "desc"
                          ? "#745D96"
                          : "#9C9C9C"
                      }
                    />
                  </svg>
                </th>
                <th
                  className="border-b p-2 cursor-pointer text-left"
                  onClick={() => handleSort("pax")}
                >
                  Pax{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="16"
                    height="32"
                    className="inline-block ml-1"
                  >
                    <path
                      d="M12 5l-5 5h10l-5-5z"
                      fill={
                        sortConfig.key === "pax" &&
                        sortConfig.direction === "asc"
                          ? "#745D96"
                          : "#9C9C9C"
                      }
                    />

                    <path
                      d="M12 19l5-5H7l5 5z"
                      fill={
                        sortConfig.key === "pax" &&
                        sortConfig.direction === "desc"
                          ? "#745D96"
                          : "#9C9C9C"
                      }
                    />
                  </svg>
                </th>
              </>
            )}
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("cancels")}
            >
              Cancels{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "cancels" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "cancels" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            {payColumn && (
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort(payColumn.key)}
              >
                {payColumn.header}{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1"
                >
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === payColumn.key &&
                      sortConfig.direction === "asc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === payColumn.key &&
                      sortConfig.direction === "desc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />
                </svg>
              </th>
            )}
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("tier")}
            >
              Trip Tier{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "tier" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "tier" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("trip_kms")}
            >
              Trip KMs{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "trip_kms" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "trip_kms" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("actual_kms")}
            >
              Actual KMs{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "actual_kms" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "actual_kms" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("comments")}
            >
              Comments{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "comments" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "comments" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item) => {
            const rowHighlight = (item.tracking === 'incomplete' || item.no_charge || item.uncosted_reason) ? 'bg-red-100' : '';

            return (
            <tr key={item.id} className={`border-t border-b p-2 cursor-pointer ${rowHighlight}`}>
              <td className="border-t border-b p-2 text-left">
                {trackingIcon(item)}
              </td>
              <td className="border-t border-b p-2 text-left">{item.date}</td>
              <td className="border-t border-b p-2 text-left">
                {item.weekday}
              </td>
              <td className="border-t border-b p-2 text-left">{item.time}</td>
              <td className="border-t border-b p-2 text-left">{item.type}</td>
              <td className="border-t border-b p-2 text-left">{item.fleet}</td>
              <td className="border-t border-b p-2 text-left">{item.driver}</td>
              {showVehicleCluster && (
                <>
                  <td className="border-t border-b p-2 text-left">
                    {renderVehicleTypeCell(item)}
                  </td>
                  <td className="border-t border-b p-2 text-left">
                    {item.occupancy}%
                  </td>
                  <td className="border-t border-b p-2 text-left">
                    {item.pax}
                  </td>
                </>
              )}
              <td className="border-t border-b p-2 text-left">
                {item.cancels}
              </td>
              {payColumn && (
                <td className="border-t border-b p-2 text-left">
                  {payColumn.value(item)}
                </td>
              )}
              <td className="border-t border-b p-2 text-left">{item.tier}</td>
              <td className="border-t border-b p-2 text-left">
                {fmt(item.trip_kms)}
              </td>
              <td className="border-t border-b p-2 text-left">
                {editingRowId === item.id ? (
                  <input
                    type="text"
                    data-testid={`actual-kms-input-${item.id}`}
                    value={formData.actual_kms}
                    onChange={(e) => handleInputChange(e, "actual_kms")}
                    className="w-full px-2 text-gray-800 bg-white border border-gray-300 rounded-md focus:outline-none"
                  />
                ) : (
                  item.actual_kms
                )}
              </td>
              <td className="border-t border-b p-2 text-left">
                {editingRowId === item.id ? (
                  <input
                    type="text"
                    value={formData.comments}
                    onChange={(e) => handleInputChange(e, "comments")}
                    maxLength={50}
                    className="w-full p-2 text-gray-800 bg-white border border-gray-300 rounded-md focus:outline-none"
                  />
                ) : (
                  item.comments
                )}
              </td>
              <td className="border-t border-b p-2">
                <div className="flex items-center" style={{ gap: "10px" }}>
                  {editingRowId === item.id ? (
                    <button
                      type="button"
                      onClick={() => handleSaveClick(item)}
                      data-testid={`save-row-${item.id}`}
                      title="Save"
                      aria-label="Save"
                      className={ACTION_BTN_CLASS}
                      style={actionBtnStyle(BTN_GREEN)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                        <path
                          d="M17 3H7C5.9 3 5 3.9 5 5v14c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-5zm-2 14H9v-2h6v2zm0-4H9v-2h6v2zm1-4V4.5L19.5 9H16z"
                          fill="white"
                        />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleEditClick(item)}
                      data-testid={`edit-row-${item.id}`}
                      title="Edit"
                      aria-label="Edit"
                      className={ACTION_BTN_CLASS}
                      style={item.approved_by !== null ? { display: "none" } : actionBtnStyle(PURPLE)}
                    >
                      <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12.5389 2.59738L15.3481 5.41668C15.4665 5.53546 15.4665 5.72924 15.3481 5.84802L8.54613 12.6744L5.65589 12.9963C5.2697 13.04 4.94268 12.7119 4.98628 12.3243L5.30707 9.42372L12.1091 2.59738C12.2274 2.47861 12.4205 2.47861 12.5389 2.59738ZM17.5843 1.88162L16.0645 0.35632C15.5911 -0.118773 14.8218 -0.118773 14.3453 0.35632L13.2428 1.46279C13.1244 1.58156 13.1244 1.77535 13.2428 1.89412L16.052 4.71342C16.1704 4.83219 16.3635 4.83219 16.4818 4.71342L17.5843 3.60695C18.0577 3.12874 18.0577 2.35671 17.5843 1.88162ZM11.9596 10.8177V13.9996H1.99327V3.99766H9.15034C9.25 3.99766 9.34343 3.95702 9.41507 3.88826L10.6609 2.63802C10.8976 2.40047 10.7294 1.99727 10.3961 1.99727H1.49495C0.669613 1.99727 0 2.66927 0 3.49756V14.4997C0 15.328 0.669613 16 1.49495 16H12.4579C13.2832 16 13.9529 15.328 13.9529 14.4997V9.56749C13.9529 9.23305 13.5511 9.0674 13.3144 9.30182L12.0686 10.5521C12.0001 10.624 11.9596 10.7177 11.9596 10.8177Z"
                          fill="white"
                        />
                      </svg>
                    </button>
                  )}

                  {editingRowId === item.id ? (
                    <button
                      type="button"
                      onClick={() => openNoChargeConfirm(item)}
                      data-testid={`no-charge-row-${item.id}`}
                      title={item.no_charge ? "Restore No Charge" : "Mark as No Charge"}
                      aria-label="No charge"
                      className={ACTION_BTN_CLASS}
                      style={actionBtnStyle(PURPLE)}
                    >
                      <img src={item.no_charge ? addFees : removeFees} alt="No Charge" className="block w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => window.open(`/grouped_trips/${item.id}`, "_blank", "noopener")}
                      data-testid={`view-row-${item.id}`}
                      title="View grouped trip"
                      aria-label="View grouped trip"
                      className={ACTION_BTN_CLASS}
                      style={actionBtnStyle(GREY)}
                    >
                      {/* Reuse the Weekly Overview eye (correct viewBox, white stroke). */}
                      <EyeToggleIcon expanded={false} />
                    </button>
                  )}

                  {editingRowId === item.id ? (
                    <button
                      type="button"
                      onClick={() => handleCancelClick()}
                      data-testid={`cancel-row-${item.id}`}
                      title="Cancel"
                      aria-label="Cancel"
                      className={ACTION_BTN_CLASS}
                      style={actionBtnStyle(BTN_RED)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        <line x1="6" y1="6" x2="18" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`revert-row-${item.id}`}
                      title="Revert"
                      aria-label="Revert"
                      className={ACTION_BTN_CLASS}
                      style={item.approved_by !== null ? { display: "none" } : actionBtnStyle(GREY)}
                    >
                      <svg width="20" height="20" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8.19871 8.67352e-06C6.06 0.00323448 4.12129 0.845169 2.68581 2.21614L1.53742 1.06452C1.05033 0.577428 0.214844 0.922589 0.214844 1.60969V5.93549C0.214844 6.36452 0.560005 6.70968 0.989037 6.70968H5.31484C6.00516 6.70968 6.35032 5.8742 5.86323 5.3871L4.51484 4.03872C5.51162 3.10646 6.79871 2.59033 8.16645 2.57743C11.1471 2.55162 13.66 4.96452 13.6342 8.04194C13.6084 10.9677 11.2406 13.4194 8.21484 13.4194C6.88903 13.4194 5.6342 12.9452 4.6471 12.0774C4.49549 11.9419 4.26323 11.9516 4.11807 12.0968L2.83742 13.3774C2.67936 13.5355 2.68904 13.7903 2.85355 13.9387C4.27291 15.2194 6.15355 16 8.21484 16C12.6342 16 16.2148 12.4194 16.2148 8C16.2148 3.5871 12.6148 -0.00644294 8.19871 8.67352e-06Z"
                          fill="white"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      <div data-testid="company-overview-pagination" className="px-4 py-3 bg-gray-50">
      <div className="flex justify-between items-center w-full">
        <span className="text-sm text-gray-700">
          Page {currentPage}
        </span>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentPage === 1}
            aria-label="Previous Page"
            className={`${
              currentPage === 1
                ? "bg-gray-500 hover:bg-gray-600"
                : "bg-purple-500 hover:bg-purple-600 "
            } 
              text-white inline-flex items-center justify-center p-2 w-9 h-9 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-70 transition-colors duration-150 ease-in-out`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={handleNext}
            disabled={!data?.next_page}
            aria-label="Next Page"
            className={`${
              !data?.next_page
                ? "bg-gray-500 hover:bg-gray-600"
                : "bg-purple-500 hover:bg-purple-600"
            }
              text-white inline-flex items-center justify-center p-2 w-9 h-9 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-70 transition-colors duration-150 ease-in-out`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      </div>
      </div>
      {showNoChargeConfirm.show && (
        <div className="fixed inset-0 flex items-center justify-center z-40">
          <div className="fixed inset-0 bg-black opacity-50" onClick={closeNoChargeConfirm} />
          <div className="bg-white w-1/3 rounded-lg shadow-lg z-10 relative p-6 mx-4 break-words whitespace-normal">
            <h2 className="text-xl font-medium mb-3" style={{ color: '#6E6C71' }}>
              {showNoChargeConfirm.item?.no_charge ? 'Restore Charge' : 'Mark as No Charge'}
            </h2>
            <p className="text-gray-600 mb-4 break-words whitespace-normal">
              {showNoChargeConfirm.item?.no_charge
                ? 'This will include the trip back into client billing and driver payments. Continue?'
                : 'This will exclude the trip from client billing and driver payments. Continue?'}
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={closeNoChargeConfirm}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded text-sm hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const item = showNoChargeConfirm.item;
                  closeNoChargeConfirm();
                  if (item) {
                    if (item.no_charge) {
                      handleMarkNoChargeRestore(item);
                    } else {
                      handleMarkNoCharge(item);
                    }
                  }
                }}
                className="px-6 py-2 bg-purple-700 text-white rounded text-sm hover:bg-purple-800"
              >
                {showNoChargeConfirm.item?.no_charge ? 'Restore' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesTable;
